// Polls GET /badges for the given warehouse and returns the full set of
// home / sub-menu badge counts. One request replaces the per-tile polling
// every page used to do; the figures are precomputed server-side, so the
// hook does no summing — pages just read the slice they render.
//
// Seeded from a per-warehouse localStorage cache so badges paint instantly
// on mount; each successful poll refreshes the cache. Transient errors are
// swallowed — the last good value stays until the next tick succeeds.

import { useEffect, useState } from "react";
import { apiFetch } from "../api/apiFetch";
import {
    BadgeCounts,
    EMPTY_BADGE_COUNTS,
    readCachedBadgeCounts,
    writeCachedBadgeCounts,
} from "../components/helpers/badgeCountCache";

const BADGE_POLL_MS = 10000;

export const useBadgeCounts = (warehouse: string | undefined): BadgeCounts => {
    const [counts, setCounts] = useState<BadgeCounts>(() =>
        warehouse ? readCachedBadgeCounts(warehouse) : EMPTY_BADGE_COUNTS,
    );

    useEffect(() => {
        if (!warehouse) {
            setCounts(EMPTY_BADGE_COUNTS);
            return;
        }
        let alive = true;
        // In-flight guard: if a poll is still pending when the next tick
        // fires (e.g. the BE is mid-restart and a request is hanging on the
        // Mongo session store), skip it rather than stacking overlapping
        // requests. The next tick retries once the current one settles.
        let inFlight = false;
        // Re-seed from cache when the warehouse changes (e.g. account switch).
        setCounts(readCachedBadgeCounts(warehouse));
        const fetchCounts = async () => {
            if (inFlight) return;
            inFlight = true;
            try {
                const res = await apiFetch(`/badges?warehouse=${encodeURIComponent(warehouse)}`);
                if (!res.ok) return;
                const data = (await res.json()) as BadgeCounts;
                if (!alive || !data || typeof data !== "object") return;
                const next = { ...EMPTY_BADGE_COUNTS, ...data };
                setCounts(next);
                writeCachedBadgeCounts(warehouse, next);
            } catch {
                // ignore transient errors; next tick will try again
            } finally {
                inFlight = false;
            }
        };
        fetchCounts();
        const id = setInterval(fetchCounts, BADGE_POLL_MS);
        return () => {
            alive = false;
            clearInterval(id);
        };
    }, [warehouse]);

    return counts;
};
